import { openExternal } from "@/lib/open-external";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { gatedRobotsMeta } from "@/lib/seo-gating";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import groundingVideo from "@/assets/ground-yourself.mp4.asset.json";
import {
  HeartHandshake,
  ArrowLeft,
  Leaf,
  Wind,
  Waves,
  Users,
  LifeBuoy,
  Sparkles,
  Heart,
  Star,
  Check,
  EyeOff,
  Undo2,
  X,
  ExternalLink,
  Phone,
  ShieldCheck,
  NotebookPen,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Trash2,
  Copy,
  Mail,
  Save,
  Sparkle,
  Moon,
  Search,
  Download,
} from "lucide-react";

import {
  SH_ALTERNATIVES,
  CATEGORY_META,
  JANA_TOP_10,
  CRISIS_BY_COUNTRY,
  CRISIS_GLOBAL,
  type Alternative,
  type AlternativeCategory,
  type CountryCrisis,
} from "@/data/learn/self-harm";
import { getProfile } from "@/lib/profile.functions";
import { SwipeDeck } from "@/components/learn/SwipeDeck";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/learn/self-harm-support")({
  validateSearch: (search) =>
    z
      .object({ done: z.enum(["1", "true"]).optional() })
      .parse(search),
  head: () => ({
    meta: [
      { title: "You Are Not Alone — Self-Harm Support · Recovery Trauma™" },
      { name: "description", content: "Gentle, trauma-informed support for moments when the urge to self-harm feels strong. Grounding, breathing, TIPP, personal safety plan and location-aware crisis signposting." },
      { name: "robots", content: "index, follow, max-snippet:-1" },
      { property: "og:title", content: "You Are Not Alone — Self-Harm Support · Recovery Trauma™" },
      { property: "og:description", content: "Calm, non-judgemental support tools for difficult moments. Not a crisis service — always includes real human support signposts." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/self-harm-support" },
      { name: "twitter:card", content: "summary_large_image" },
      ...gatedRobotsMeta("/learn/self-harm-support"),
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/self-harm-support" }],
  }),
  component: SelfHarmPage,
});

// ---------------------- local storage helpers ----------------------

const LS_FAVS = "rt:sh:favs:v1";
const LS_HELPFUL = "rt:sh:helpful:v1";
const LS_HIDDEN = "rt:sh:hidden:v1";
const LS_SAFETY = "rt:sh:safety-list:v1";
const LS_PLAN = "rt:sh:safety-plan:v1";
const LS_CONTACTS = "rt:sh:safe-contacts:v1";

function useSet(key: string) {
  const [set, setSet] = useState<Set<string>>(new Set());
  useEffect(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) setSet(new Set(JSON.parse(raw)));
    } catch { /* ignore */ }
  }, [key]);
  const toggle = (id: string) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try { localStorage.setItem(key, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };
  const clear = () => {
    setSet(new Set());
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  };
  return { set, toggle, clear };
}

// ---------------------- Page ----------------------

function SelfHarmPage() {
  const [crisisOpen, setCrisisOpen] = useState(false);
  const [completionOpen, setCompletionOpen] = useState(false);
  const search = useSearch({ from: "/learn/self-harm-support" }) as { done?: string };

  useEffect(() => {
    if (search.done === "1" || search.done === "true") setCompletionOpen(true);
  }, [search.done]);

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const [tab, setTab] = useState<SectionTabId>("right-now");
  const [query, setQuery] = useState("");

  return (
    <main className="min-h-dvh bg-gradient-to-b from-[#fbf5ee] via-[#fdf7f0] to-[#f6efe6] px-4 pb-24 pt-8 dark:from-[oklch(0.26_0.02_80)] dark:via-[oklch(0.26_0.02_80)] dark:to-[oklch(0.26_0.02_80)]">
      <div className="mx-auto max-w-3xl">
        <Link
          to="/learn"
          className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Learn
        </Link>

        <h1 className="mb-4 font-serif text-3xl tracking-tight text-foreground sm:text-4xl">
          You are not alone
        </h1>
        <p className="mb-6 text-sm leading-relaxed text-foreground/75">
          Gentle self-harm support: grounding, ways to ride out an urge, safety
          planning and crisis contacts. Take whatever is useful, at your pace.
        </p>

        <ClosingNote />
        <GroundingVideo />
        <TopSearch
          value={query}
          onChange={(v) => {
            setQuery(v);
            if (v.trim()) setTab("ride-it-out");
          }}
        />
        <SectionTabs active={tab} onChange={setTab} />

        {tab === "crisis" && (
          <>
            <CrisisBanner onCrisis={() => setCrisisOpen(true)} />
            <FooterSafety onCrisis={() => setCrisisOpen(true)} />
          </>
        )}

        {tab === "right-now" && (
          <>
            <RightNowTriage
              onCrisis={() => setCrisisOpen(true)}
              onJump={(id) => setTab(idToTab(id))}
            />
            <PrimaryActions
              onCrisis={() => setCrisisOpen(true)}
              onOpenPlan={() => setTab("safety-plan")}
              onOpenContacts={() => setTab("reach-out")}
              onCompletion={() => setCompletionOpen(true)}
            />
            <OpeningMessage />
          </>
        )}

        {tab === "ride-it-out" && <AlternativesSection query={query} />}

        {tab === "safety-plan" && (
          <SafetyPlanSection
            onCrisis={() => setCrisisOpen(true)}
            onOpenContacts={() => setTab("reach-out")}
          />
        )}

        {tab === "reach-out" && <ContactSafeSection />}

        {tab === "jana" && (
          <>
            <JanaSection />
            <GroundingVideo />
          </>
        )}

        <Disclaimer />
      </div>

      <FloatingGroundMeNow />

      {crisisOpen && <CrisisPanel onClose={() => setCrisisOpen(false)} />}
      {completionOpen && (
        <CompletionPrompt
          onClose={() => setCompletionOpen(false)}
          onCrisis={() => {
            setCompletionOpen(false);
            setCrisisOpen(true);
          }}
          onOpenPlan={() => {
            setCompletionOpen(false);
            setTab("safety-plan");
          }}
          onOpenContacts={() => {
            setCompletionOpen(false);
            setTab("reach-out");
          }}
        />
      )}
    </main>
  );
}

// ---------------------- Grounding video ----------------------

function GroundingVideo() {
  return (
    <section aria-label="Grounding video" className="mb-6">
      <div className="overflow-hidden rounded-3xl border border-[#e6d7bd] bg-white/80 p-4 shadow-sm dark:border-white/12 dark:bg-white/10 sm:p-5">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#fbf5ee] px-3 py-1 text-xs text-[#8a6d3a] ring-1 ring-[#e6d7bd] dark:bg-white/10 dark:text-[oklch(0.86_0.09_85)] dark:ring-white/12">
          <Leaf className="h-3.5 w-3.5" /> Grounding video
        </div>
        <div className="overflow-hidden rounded-2xl bg-black/5 dark:bg-black/30">
          {/* Replace the src below with a new asset pointer to swap the video. */}
          <video
            src={groundingVideo.url}
            controls
            playsInline
            preload="metadata"
            className="aspect-video h-auto w-full"
          >
            Your browser does not support embedded video.
          </video>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
          A short grounding practice you can follow along with — no headphones
          needed. Pause it any time.
        </p>
      </div>
    </section>
  );
}

// ---------------------- Search ----------------------

function TopSearch({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="mb-5">
      <div className="relative">
        <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#b99a5f]" />
        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, 80))}
          maxLength={80}
          placeholder="Search gentle ideas — cold water, breathe, music…"
          aria-label="Search self-harm support ideas"
          className="w-full rounded-full border border-[#e6d7bd] bg-white/90 py-3.5 ps-11 pe-11 text-sm text-foreground shadow-sm outline-none transition placeholder:text-muted-foreground/70 focus:border-[#c9a24a] focus:ring-2 focus:ring-[#c9a24a]/30 dark:border-white/12 dark:bg-white/10"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="Clear search"
            className="absolute end-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition hover:bg-[#fbf5ee] hover:text-foreground dark:hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------- Section tabs ----------------------

type SectionTabId =
  | "crisis"
  | "right-now"
  | "ride-it-out"
  | "safety-plan"
  | "reach-out"
  | "jana";

const SECTION_TABS: { id: SectionTabId; label: string; icon: typeof Leaf }[] = [
  { id: "crisis", label: "Crisis Help", icon: LifeBuoy },
  { id: "right-now", label: "Right Now", icon: Sparkle },
  { id: "ride-it-out", label: "Ride It Out", icon: Waves },
  { id: "safety-plan", label: "Safety Plan", icon: NotebookPen },
  { id: "reach-out", label: "Reach Out", icon: Users },
  { id: "jana", label: "Jana's Tools", icon: Heart },
];

function idToTab(id: string): SectionTabId {
  if (id === "contact-safe") return "reach-out";
  if (id === "jana") return "jana";
  if (id === "safety-plan") return "safety-plan";
  if (id === "ride-it-out") return "ride-it-out";
  return "right-now";
}

function SectionTabs({
  active,
  onChange,
}: {
  active: SectionTabId;
  onChange: (id: SectionTabId) => void;
}) {
  return (
    <nav
      aria-label="Support sections"
      className="sticky top-2 z-30 mb-6 -mx-1 rounded-2xl border border-[#e6d7bd] bg-white/85 px-2 py-2 shadow-sm backdrop-blur dark:border-white/12 dark:bg-[oklch(0.26_0.02_80)]/85"
    >
      <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {SECTION_TABS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              aria-current={isActive ? "true" : undefined}
              onClick={() => onChange(t.id)}
              className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a24a] ${
                isActive
                  ? "bg-[#8a6d3a] text-white shadow-sm"
                  : "bg-[#fbf5ee] text-[#8a6d3a] ring-1 ring-[#ecdfc9] hover:bg-[#f4e9d6] dark:bg-white/10 dark:text-[oklch(0.86_0.09_85)] dark:ring-white/12"
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" /> {t.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ---------------------- Right now triage ----------------------

interface TriageStep {
  label: string;
  to?: string;
  hash?: string;
  jump?: string;
  crisis?: boolean;
}

interface TriageState {
  id: string;
  label: string;
  sub: string;
  icon: typeof Leaf;
  body: string;
  steps: TriageStep[];
}

const TRIAGE_STATES: TriageState[] = [
  {
    id: "unsafe",
    label: "I don't feel safe",
    sub: "I might act on this",
    icon: LifeBuoy,
    body: "Please let a real person be with you in this. You do not have to explain it well, and you do not have to be in danger to deserve help.",
    steps: [
      { label: "Open crisis support", crisis: true },
      { label: "Contact someone safe", jump: "contact-safe" },
      { label: "Open my safety plan", jump: "safety-plan" },
    ],
  },
  {
    id: "urge",
    label: "The urge is strong",
    sub: "But I want to wait it out",
    icon: Waves,
    body: "Urges rise and fall. You do not have to make them disappear — only stay with yourself while this one passes.",
    steps: [
      { label: "Ride the urge", to: "/learn/glossary", hash: "Urge-Surfing" },
      { label: "Try TIPP skills", to: "/learn/dbt" },
      { label: "Softer things to reach for", jump: "ride-it-out" },
    ],
  },
  {
    id: "overwhelmed",
    label: "I'm overwhelmed",
    sub: "Too much, too fast",
    icon: Wind,
    body: "When everything is too loud inside, the body settles before the mind does. Start with breath, slowly.",
    steps: [
      { label: "Guided breathing", to: "/breathing" },
      { label: "Ground me now", to: "/grounding" },
      { label: "Softer things to reach for", jump: "ride-it-out" },
    ],
  },
  {
    id: "numb",
    label: "I feel numb",
    sub: "Flat, far away, unreal",
    icon: Leaf,
    body: "Numbness is protection, not failure. Gentle sensation can bring you back without hurting you.",
    steps: [
      { label: "Ground me now", to: "/grounding" },
      { label: "Sensory alternatives", jump: "ride-it-out" },
      { label: "Contact someone safe", jump: "contact-safe" },
    ],
  },
  {
    id: "after",
    label: "I've already hurt myself",
    sub: "I need aftercare",
    icon: HeartHandshake,
    body: "You are not in trouble here. Care for the wound, tell someone if you can, and let the next hour be gentle. Seek medical help if there is any doubt about the injury.",
    steps: [
      { label: "Crisis and medical help", crisis: true },
      { label: "Contact someone safe", jump: "contact-safe" },
      { label: "Something soft to do next", jump: "ride-it-out" },
    ],
  },
  {
    id: "steady",
    label: "I'm steady today",
    sub: "I want to prepare",
    icon: ShieldCheck,
    body: "Calm moments are the best time to build the plan you will lean on later.",
    steps: [
      { label: "Build my safety plan", jump: "safety-plan" },
      { label: "Add safe contacts", jump: "contact-safe" },
      { label: "Jana's top 10 tools", jump: "jana" },
    ],
  },
];

function RightNowTriage({
  onCrisis,
  onJump,
}: {
  onCrisis: () => void;
  onJump: (id: string) => void;
}) {
  const [active, setActive] = useState<string | null>(null);
  const state = TRIAGE_STATES.find((s) => s.id === active) ?? null;

  return (
    <section
      id="right-now"
      aria-labelledby="right-now-heading"
      className="mb-8 scroll-mt-24"
    >
      <div className="mb-4 text-center">
        <h2
          id="right-now-heading"
          className="font-serif text-2xl tracking-tight text-foreground sm:text-3xl"
        >
          How are you right now?
        </h2>
        <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-foreground/75">
          Choose whichever is closest. Nothing is recorded, and you can change
          your mind at any point.
        </p>
      </div>

      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {TRIAGE_STATES.map((s) => {
          const Icon = s.icon;
          const selected = active === s.id;
          return (
            <button
              key={s.id}
              type="button"
              aria-pressed={selected}
              onClick={() => setActive(selected ? null : s.id)}
              className={`flex items-start gap-3 rounded-2xl px-4 py-3.5 text-start transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a24a] focus-visible:ring-offset-2 ${
                selected
                  ? "bg-gradient-to-r from-[#c9a24a] to-[#d9b866] text-white shadow-md ring-1 ring-[#b8912f]"
                  : "bg-white text-foreground shadow-sm ring-1 ring-[#ecdfc9] hover:-translate-y-0.5 hover:shadow-md dark:bg-card dark:ring-white/10"
              }`}
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                  selected ? "bg-white/20 text-white" : "bg-[#fbf5ee] text-[#8a6d3a]"
                }`}
              >
                <Icon className="h-4.5 w-4.5" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block text-[15px] font-medium leading-snug">
                  {s.label}
                </span>
                <span
                  className={`block text-xs leading-snug ${
                    selected ? "text-white/85" : "text-foreground/60"
                  }`}
                >
                  {s.sub}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {state && (
        <div
          role="region"
          aria-live="polite"
          aria-label={`Suggested next steps for: ${state.label}`}
          className="mt-4 rounded-3xl border border-[#e6d7bd] bg-white/80 p-5 shadow-sm dark:border-white/12 dark:bg-white/10"
        >
          <p className="text-[15px] leading-relaxed text-foreground/85">
            {state.body}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {state.steps.map((step) => {
              const cls =
                "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a24a] focus-visible:ring-offset-2 " +
                (step.crisis
                  ? "bg-[#8a3b3b] text-white hover:bg-[#7a3232]"
                  : "bg-[#fbf5ee] text-[#8a6d3a] ring-1 ring-[#ecdfc9] hover:bg-[#f4e9d6] dark:bg-white/10 dark:text-[oklch(0.86_0.09_85)] dark:ring-white/12");
              if (step.crisis) {
                return (
                  <button key={step.label} type="button" onClick={onCrisis} className={cls}>
                    <LifeBuoy className="h-4 w-4" aria-hidden="true" /> {step.label}
                  </button>
                );
              }
              if (step.jump) {
                return (
                  <button
                    key={step.label}
                    type="button"
                    onClick={() => onJump(step.jump as string)}
                    className={cls}
                  >
                    {step.label}
                  </button>
                );
              }
              return (
                <Link
                  key={step.label}
                  to={step.to as "/grounding"}
                  hash={step.hash as string | undefined}
                  className={cls}
                >
                  {step.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

function Disclaimer() {

  return (
    <aside
      role="note"
      aria-label="Important safety disclaimer"
      className="mb-5 rounded-2xl border border-[#e6d7bd] bg-white/70 dark:bg-white/10 p-4 text-sm leading-relaxed text-foreground/85 dark:border-white/12"
    >
      <div className="mb-1 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[#8a6d3a] dark:text-[oklch(0.86_0.09_85)]">
        <ShieldCheck className="h-3.5 w-3.5" /> Please read
      </div>
      Recovery Trauma™ provides educational and wellbeing support. It is not a
      replacement for professional mental health care, crisis support, or
      emergency services.
    </aside>
  );
}

function OpeningMessage() {
  return (
    <section
      aria-label="Opening message"
      className="mb-6 rounded-3xl border border-[#e6d7bd] bg-gradient-to-br from-white dark:from-[oklch(0.26_0.02_80)] via-[#fbf5ee] to-[#f4e9d6] p-6 shadow-sm sm:p-7 dark:border-white/12 dark:via-[oklch(0.26_0.02_80)] dark:to-[oklch(0.28_0.025_80)]"
    >
      <div className="space-y-3 text-[15px] leading-relaxed text-foreground/90">
        <p>
          If you are here because you are struggling,{" "}
          <span className="font-medium">thank you for opening this page.</span>
        </p>
        <p>We will not shame you, judge you, or tell you that you have failed.</p>
        <p>
          Self-harm can sometimes become a way of coping with intense emotional
          pain, numbness, overwhelm, anger, fear, shame, or memories that feel
          too difficult to hold.
        </p>
        <p>
          That does not mean you are weak, broken, attention-seeking, or beyond
          help.
        </p>
        <p>
          You do not have to promise that you will never struggle again. For
          now, let us focus only on helping you get through this moment.
        </p>
        <p className="font-serif text-xl text-[#8a6d3a] dark:text-[oklch(0.86_0.09_85)]">You are not alone.</p>
      </div>
    </section>
  );
}

// ---------------------- Primary actions ----------------------

interface ActionDef {
  label: string;
  icon: typeof Leaf;
  to?: string;
  hash?: string;
  onClick?: () => void;
  primary?: boolean;
  crisis?: boolean;
}

function PrimaryActions({
  onCrisis,
  onOpenPlan,
  onOpenContacts,
  onCompletion,
}: {
  onCrisis: () => void;
  onOpenPlan: () => void;
  onOpenContacts: () => void;
  onCompletion: () => void;
}) {
  const actions: ActionDef[] = [
    { label: "Ground Me Now", icon: Leaf, to: "/grounding", primary: true },
    { label: "Start Guided Breathing", icon: Wind, to: "/breathing" },
    { label: "Try TIPP Skills", icon: Sparkles, to: "/learn/dbt" },
    { label: "Ride the Urge", icon: Waves, to: "/learn/glossary", hash: "Urge-Surfing" },
    { label: "My Safety Plan", icon: NotebookPen, onClick: onOpenPlan },
    { label: "Contact Someone Safe", icon: Users, onClick: onOpenContacts },
    { label: "Crisis Support", icon: LifeBuoy, onClick: onCrisis, crisis: true },
  ];

  return (
    <section aria-label="Immediate safety actions" className="mb-10">
      <div className="grid gap-3 sm:grid-cols-2">
        {actions.map((a) => (
          <ActionButton key={a.label} action={a} />
        ))}
      </div>
      <div className="mt-3 text-center">
        <button
          type="button"
          onClick={onCompletion}
          className="text-xs text-[#8a6d3a] underline underline-offset-2 hover:text-[#725a2d] dark:text-[oklch(0.86_0.09_85)]"
        >
          I just finished a step — mark this moment
        </button>
      </div>
    </section>
  );
}

function ActionButton({ action }: { action: ActionDef }) {
  const Icon = action.icon;
  const base =
    "group flex items-center gap-3 rounded-2xl px-5 py-4 text-left text-[15px] font-medium shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a24a] focus-visible:ring-offset-2";
  const styles = action.primary
    ? "col-span-full bg-gradient-to-r from-[#c9a24a] via-[#d9b866] to-[#c9a24a] text-white ring-1 ring-[#b8912f]"
    : action.crisis
      ? "bg-white text-[#8a6d3a] ring-1 ring-[#e6d7bd] hover:bg-[#fbf5ee] dark:bg-card dark:text-[oklch(0.86_0.09_85)] dark:ring-white/15 dark:hover:bg-card/80"
      : "bg-white text-foreground ring-1 ring-[#ecdfc9] hover:bg-[#fdf9f2] dark:bg-card dark:ring-white/10 dark:hover:bg-card/80";

  const inner = (
    <>
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
          action.primary
            ? "bg-white/20 dark:bg-white/10 text-white"
            : "bg-[#fbf5ee] text-[#8a6d3a]"
        }`}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="flex-1">{action.label}</span>
    </>
  );

  if (action.onClick) {
    return (
      <button type="button" onClick={action.onClick} className={`${base} ${styles}`}>
        {inner}
      </button>
    );
  }
  return (
    <Link
      to={action.to as "/grounding"}
      hash={action.hash as string | undefined}
      className={`${base} ${styles}`}
    >
      {inner}
    </Link>
  );
}

// ---------------------- Floating Ground Me Now ----------------------

function FloatingGroundMeNow() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 320);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <Link
      to="/grounding"
      aria-label="Ground Me Now"
      className={`fixed bottom-5 end-5 z-40 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#c9a24a] to-[#d9b866] px-5 py-3 text-sm font-medium text-white shadow-lg ring-1 ring-[#b8912f] transition-all ${
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
      }`}
    >
      <Leaf className="h-4 w-4" />
      Ground Me Now
    </Link>
  );
}

// ---------------------- Crisis country ----------------------

function useCrisisCountry(): CountryCrisis {
  const { data } = useQuery({
    queryKey: ["profile-country"],
    queryFn: async () => {
      try {
        const res = await getProfile();
        return (res?.profile as { country?: string | null } | null)?.country ?? null;
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
  });
  return (data && CRISIS_BY_COUNTRY[data.toUpperCase()]) || CRISIS_GLOBAL;
}

/** Turn "Call 116 123 · free, 24/7" or "Text SHOUT to 85258" into a tel:/sms: link. */
function actionLinkFor(detail: string): { href: string; label: string } | null {
  const sms = detail.match(/Text\s+([A-Z0-9]+)\s+to\s+([\d\s]{3,})/i);
  if (sms) return { href: `sms:${sms[2].replace(/\s/g, "")}?&body=${encodeURIComponent(sms[1])}`, label: `Text ${sms[1]}` };
  const tel = detail.match(/(?:Call(?:\s+or\s+text)?|Dial)\s+([\d\s()+-]{3,})/i);
  if (tel) {
    const num = tel[1].replace(/[^\d+]/g, "");
    if (num.length >= 3) return { href: `tel:${num}`, label: `Call ${tel[1].trim()}` };
  }
  return null;
}

// ---------------------- Crisis banner ----------------------

function CrisisBanner({ onCrisis }: { onCrisis: () => void }) {
  const countryInfo = useCrisisCountry();
  const emergencyDigits = countryInfo.emergency.replace(/[^\d]/g, "");

  return (
    <section
      aria-labelledby="crisis-banner-title"
      className="mb-6 overflow-hidden rounded-3xl border-2 border-[#e3b7ab] bg-gradient-to-br from-[#fff6f3] via-[#fdf1ec] to-[#fbe8e1] shadow-[0_10px_30px_-18px_rgba(160,90,70,0.55)] dark:border-white/15 dark:from-[oklch(0.26_0.03_30)] dark:via-[oklch(0.27_0.03_30)] dark:to-[oklch(0.28_0.035_30)]"
    >
      <div className="px-5 py-5 sm:px-7 sm:py-6">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/80 dark:bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#a4553f] dark:text-[oklch(0.84_0.08_35)] ring-1 ring-[#eccfc5] dark:ring-white/12">
          <LifeBuoy className="h-3.5 w-3.5" /> Crisis resources
        </div>

        <h2 id="crisis-banner-title" className="font-serif text-xl text-foreground sm:text-2xl">
          If you are in danger right now, please reach a real person
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-foreground/80">
          Call emergency services on{" "}
          <span className="font-semibold text-foreground">{countryInfo.emergency}</span> in{" "}
          {countryInfo.countryName}, go to your nearest emergency department, or ask someone you
          trust to stay with you until the moment passes. You do not have to explain it well —
          just say you need help staying safe.
        </p>

        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {countryInfo.resources.slice(0, 4).map((r) => {
            const action = actionLinkFor(r.detail);
            return (
              <li
                key={r.label}
                className="rounded-2xl border border-[#eccfc5] bg-white/85 dark:bg-white/10 p-3 text-sm dark:border-white/12"
              >
                <div className="font-medium text-foreground">{r.label}</div>
                <div className="mt-0.5 text-xs leading-relaxed text-foreground/70">{r.detail}</div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                  {action && (
                    <a
                      href={action.href}
                      className="inline-flex items-center gap-1 font-medium text-[#a4553f] dark:text-[oklch(0.84_0.08_35)] underline underline-offset-2"
                    >
                      {action.label.startsWith("Text") ? (
                        <MessageCircle className="h-3 w-3" />
                      ) : (
                        <Phone className="h-3 w-3" />
                      )}
                      {action.label}
                    </a>
                  )}
                  {r.href && (
                    <a
                      href={r.href}
                      onClick={(e) => openExternal(r.href!, e)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-muted-foreground underline underline-offset-2 hover:text-foreground"
                    >
                      Website <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        <div className="mt-4">
          <button
            type="button"
            onClick={onCrisis}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#e3b7ab] bg-white/85 dark:bg-white/10 px-5 py-3 text-sm font-medium text-[#a4553f] dark:text-[oklch(0.84_0.08_35)] transition-transform hover:scale-[1.01] dark:border-white/15"
          >
            <LifeBuoy className="h-4 w-4" /> More crisis support
          </button>
        </div>

        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
          Not in {countryInfo.countryName}?{" "}
          <a
            href="https://findahelpline.com/"
            onClick={(e) => openExternal("https://findahelpline.com/", e)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#a4553f] dark:text-[oklch(0.84_0.08_35)] underline underline-offset-2"
          >
            Find a verified helpline in your country
          </a>
          . Recovery Trauma™ is not an emergency service.
        </p>
      </div>
    </section>
  );
}

// ---------------------- Crisis Panel ----------------------

function CrisisPanel({ onClose }: { onClose: () => void }) {
  const countryInfo = useCrisisCountry();


  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-[#2a1e0a]/40 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="crisis-panel-title"
    >
      <div
        className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-[#e6d7bd] bg-gradient-to-br from-white dark:from-[oklch(0.26_0.02_80)] via-[#fbf5ee] to-[#f4e9d6] shadow-2xl sm:rounded-3xl dark:border-white/12 dark:via-[oklch(0.26_0.02_80)] dark:to-[oklch(0.28_0.025_80)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-7 sm:px-8">
          <div className="mb-3 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 dark:bg-white/10 px-3 py-1 text-xs font-medium text-[#8a6d3a] dark:text-[oklch(0.86_0.09_85)]">
              <Heart className="h-3.5 w-3.5" /> You are not alone
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-1.5 text-muted-foreground hover:bg-white/70 dark:hover:bg-white/10 dark:bg-white/10 hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <h2 id="crisis-panel-title" className="font-serif text-2xl text-foreground sm:text-3xl">
            Are you in immediate danger?
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground/85">
            Are you worried that you may seriously hurt yourself? Please
            contact one of the following:
          </p>

          <ul className="mt-4 space-y-2 text-sm text-foreground/90">
            <li className="flex gap-2"><span aria-hidden>•</span> Your local emergency services</li>
            <li className="flex gap-2"><span aria-hidden>•</span> A local crisis service</li>
            <li className="flex gap-2"><span aria-hidden>•</span> A trusted person who can stay with you</li>
            <li className="flex gap-2"><span aria-hidden>•</span> Your therapist, GP, or mental health team</li>
            <li className="flex gap-2"><span aria-hidden>•</span> The nearest emergency department</li>
          </ul>

          <p className="mt-4 rounded-xl bg-white/70 dark:bg-white/10 p-3 text-sm text-foreground/85">
            You deserve real human support. Reaching out is not wasting
            anyone's time.
          </p>

          <div className="mt-5 rounded-2xl border border-[#e6d7bd] bg-white/80 dark:bg-white/10 p-4 dark:border-white/12">
            <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[#8a6d3a] dark:text-[oklch(0.86_0.09_85)]">
              <Phone className="h-3.5 w-3.5" />
              Support in {countryInfo.countryName}
            </div>
            <div className="mb-3 text-sm">
              <span className="font-medium">Emergency:</span> {countryInfo.emergency}
            </div>
            <ul className="space-y-2 text-sm text-foreground/90">
              {countryInfo.resources.map((r) => (
                <li key={r.label}>
                  <div className="font-medium">{r.label}</div>
                  <div className="text-foreground/75">
                    {r.detail}{" "}
                    {r.href && (
                      <a
                        href={r.href}
                        onClick={(e) => openExternal(r.href!, e)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#8a6d3a] underline underline-offset-2 dark:text-[oklch(0.86_0.09_85)]"
                      >
                        Visit <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {countryInfo !== CRISIS_GLOBAL && (
              <p className="mt-3 text-xs text-muted-foreground">
                Not in {countryInfo.countryName}?{" "}
                <a
                  href="https://findahelpline.com/"
                  onClick={(e) => openExternal("https://findahelpline.com/", e)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#8a6d3a] underline underline-offset-2 dark:text-[oklch(0.86_0.09_85)]"
                >
                  Find a helpline in your country
                </a>
              </p>
            )}
          </div>




          <p className="mt-4 text-center text-xs text-muted-foreground">
            Recovery Trauma™ is not an emergency service. Our community, AI
            chat, and platform cannot replace professional or crisis support.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------- Alternatives ----------------------

const CATEGORY_ORDER: AlternativeCategory[] = [
  "immediate",
  "grounding",
  "movement",
  "creative",
  "comfort",
  "connection",
  "reflection",
  "rest",
];

function AlternativesSection({ query = "" }: { query?: string }) {
  const favs = useSet(LS_FAVS);
  const helpful = useSet(LS_HELPFUL);
  const hidden = useSet(LS_HIDDEN);
  const safety = useSet(LS_SAFETY);
  const [activeCat, setActiveCat] = useState<AlternativeCategory | "all" | "favs" | "safety">("all");
  const [showHidden, setShowHidden] = useState(false);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SH_ALTERNATIVES.filter((a) => {
      if (activeCat === "favs") return favs.set.has(a.id);
      if (activeCat === "safety") return safety.set.has(a.id);
      if (activeCat !== "all" && a.category !== activeCat) return false;
      if (!showHidden && hidden.set.has(a.id)) return false;
      if (q && !a.text.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [activeCat, favs.set, safety.set, hidden.set, showHidden, query]);

  const usedCats = CATEGORY_ORDER.filter((c) => SH_ALTERNATIVES.some((a) => a.category === c));

  return (
    <section id="ride-it-out" aria-labelledby="alternatives-heading" className="mb-12 scroll-mt-24">
      <div className="mb-4 text-center">
        <h2
          id="alternatives-heading"
          className="font-serif text-3xl tracking-tight text-foreground"
        >
          Softer Things to Reach For
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-foreground/75">
          None of these may feel as immediate as an urge. That is
          understandable. Trying even one, for a few minutes, is meaningful.
        </p>
      </div>




      <div className="mb-4 flex flex-wrap justify-center gap-2">
        <Chip active={activeCat === "all"} onClick={() => setActiveCat("all")}>
          All
        </Chip>
        <Chip active={activeCat === "favs"} onClick={() => setActiveCat("favs")}>
          <Star className="me-1.5 h-3.5 w-3.5" /> Favourites ({favs.set.size})
        </Chip>
        <Chip active={activeCat === "safety"} onClick={() => setActiveCat("safety")}>
          <ShieldCheck className="me-1.5 h-3.5 w-3.5" /> My safety list ({safety.set.size})
        </Chip>
        {usedCats.map((c) => (
          <Chip
            key={c}
            active={activeCat === c}
            onClick={() => setActiveCat(c)}
          >
            <span className="me-1.5">{CATEGORY_META[c].emoji}</span>
            {CATEGORY_META[c].label}
          </Chip>
        ))}
      </div>

      <p className="mb-4 text-center text-xs text-muted-foreground">
        Showing {visible.length} idea{visible.length === 1 ? "" : "s"}
        {query ? ` for “${query.trim()}”` : ""}
      </p>


      {hidden.set.size > 0 && (
        <div className="mb-3 text-center text-xs text-muted-foreground">
          {hidden.set.size} idea{hidden.set.size === 1 ? "" : "s"} hidden ·{" "}
          <button
            className="underline underline-offset-2"
            onClick={() => setShowHidden((v) => !v)}
          >
            {showHidden ? "hide again" : "show hidden"}
          </button>{" "}
          ·{" "}
          <button className="underline underline-offset-2" onClick={hidden.clear}>
            reset
          </button>
        </div>
      )}

      {visible.length === 0 ? (
        <p className="rounded-2xl border border-[#e6d7bd] bg-white/60 dark:bg-white/10 p-6 text-center text-sm text-muted-foreground dark:border-white/12">
          Nothing here yet. Try another category, or add favourites and
          personal safety-list picks from any card.
        </p>
      ) : (
        <SwipeDeck
          label="Softer things to reach for"
          twoUpOnDesktop
          slides={visible.map((a) => (
            <AlternativeCard
              key={a.id}
              item={a}
              isFav={favs.set.has(a.id)}
              isHelpful={helpful.set.has(a.id)}
              inSafety={safety.set.has(a.id)}
              isHidden={hidden.set.has(a.id)}
              onToggleFav={() => favs.toggle(a.id)}
              onToggleHelpful={() => helpful.toggle(a.id)}
              onToggleSafety={() => safety.toggle(a.id)}
              onHide={() => hidden.toggle(a.id)}
            />
          ))}
        />
      )}
    </section>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center rounded-full px-4 py-2 text-xs font-medium transition-all duration-200 active:scale-[0.97] ${
        active
          ? "bg-gradient-to-br from-[#a3813f] to-[#7c6130] text-white shadow-[0_4px_14px_-4px_rgba(138,109,58,0.55)]"
          : "bg-white/80 text-foreground/80 ring-1 ring-[#e6d7bd] hover:-translate-y-0.5 hover:bg-white hover:shadow-sm dark:bg-card/70 dark:ring-white/12 dark:hover:bg-card"
      }`}
    >
      {children}
    </button>
  );

}

function AlternativeCard({
  item,
  isFav,
  isHelpful,
  inSafety,
  isHidden,
  onToggleFav,
  onToggleHelpful,
  onToggleSafety,
  onHide,
}: {
  item: Alternative;
  isFav: boolean;
  isHelpful: boolean;
  inSafety: boolean;
  isHidden: boolean;
  onToggleFav: () => void;
  onToggleHelpful: () => void;
  onToggleSafety: () => void;
  onHide: () => void;
}) {
  const meta = CATEGORY_META[item.category];
  return (
    <div
      className={`group relative flex w-full flex-col rounded-2xl border p-4 shadow-sm transition ${
        isHidden
          ? "border-dashed border-[#e6d7bd] bg-white/50 dark:bg-white/10 opacity-70"
          : "border-[#ecdfc9] bg-white/80 hover:bg-white dark:border-white/10 dark:bg-card/70 dark:hover:bg-card"
      }`}
    >
      <div className="mb-2 inline-flex w-fit items-center gap-1 rounded-full bg-[#fbf5ee] px-2 py-0.5 text-[11px] text-[#8a6d3a] dark:bg-[oklch(0.26_0.02_80)] dark:text-[oklch(0.86_0.09_85)]">
        <span>{meta.emoji}</span>
        {meta.label}
      </div>
      <p className="text-sm leading-relaxed text-foreground/90">{item.text}</p>

      {item.to && (
        <Link
          to={item.to as "/grounding"}
          hash={item.hash as string | undefined}
          className="mt-3 inline-flex w-fit items-center gap-1 text-xs font-medium text-[#8a6d3a] hover:underline dark:text-[oklch(0.86_0.09_85)]"
        >
          Open tool →
        </Link>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
        <IconToggle
          active={isFav}
          onClick={onToggleFav}
          label={isFav ? "Remove favourite" : "Save as favourite"}
          Icon={Star}
        />
        <IconToggle
          active={isHelpful}
          onClick={onToggleHelpful}
          label={isHelpful ? "Marked helpful" : "Mark as helpful"}
          Icon={Check}
        />
        <IconToggle
          active={inSafety}
          onClick={onToggleSafety}
          label={inSafety ? "In your safety list" : "Add to my safety list"}
          Icon={ShieldCheck}
        />
        <button
          type="button"
          onClick={onHide}
          aria-label={isHidden ? "Unhide" : "Not for me"}
          className="ms-auto inline-flex items-center gap-1 rounded-full px-2 py-1 text-muted-foreground hover:bg-[#fbf5ee] hover:text-foreground dark:hover:bg-accent"
        >
          {isHidden ? <Undo2 className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          <span>{isHidden ? "Unhide" : "Not for me"}</span>
        </button>
      </div>
    </div>
  );
}

function IconToggle({
  active,
  onClick,
  label,
  Icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  Icon: typeof Star;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      title={label}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 transition ${
        active
          ? "bg-[#8a6d3a] text-white"
          : "text-muted-foreground hover:bg-[#fbf5ee] hover:text-foreground"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

// ---------------------- Jana's Top 10 ----------------------

function JanaSection() {
  return (
    <section id="jana" aria-labelledby="jana-heading" className="mb-12 scroll-mt-24">
      <div className="mb-5 text-center">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/70 dark:bg-white/10 px-3 py-1 text-xs text-[#8a6d3a] ring-1 ring-[#e6d7bd] dark:text-[oklch(0.86_0.09_85)] dark:ring-white/12">
          <Heart className="h-3.5 w-3.5" /> Founder's note
        </div>
        <h2
          id="jana-heading"
          className="font-serif text-3xl tracking-tight text-foreground"
        >
          Jana's Personal Top 10 Recovery Tools
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-sm italic text-foreground/70">
          A personal note from Jana, Founder of Recovery Trauma™
        </p>
      </div>

      <div className="mb-5 rounded-2xl border border-[#e6d7bd] bg-white/70 dark:bg-white/10 p-5 text-[15px] leading-relaxed text-foreground/85 dark:border-white/12">
        These are some of the practices I returned to during my own wellbeing
        journey. They will not be right for everyone, and they are not a
        substitute for professional help, but they helped me create small
        moments of safety, expression, and connection.
        <p className="mt-3 font-medium text-foreground/90">
          You do not have to try everything. Choose one small thing that feels
          possible today.
        </p>
      </div>

      <SwipeDeck
        label="Jana's personal top 10 recovery tools"
        ordered
        twoUpOnDesktop
        slides={JANA_TOP_10.map((item, i) => (
          <div
            key={item.id}
            className="flex w-full flex-col rounded-2xl border border-[#ecdfc9] bg-gradient-to-br from-white dark:from-[oklch(0.26_0.02_80)] via-[#fbf5ee] to-[#f4e9d6] p-5 shadow-sm dark:border-white/10 dark:via-[oklch(0.26_0.02_80)] dark:to-[oklch(0.28_0.025_80)]"
          >
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/80 dark:bg-white/10 text-lg ring-1 ring-[#e6d7bd] dark:ring-white/12">
                {item.emoji}
              </span>
              <div className="text-xs uppercase tracking-wider text-[#8a6d3a] dark:text-[oklch(0.86_0.09_85)]">
                {String(i + 1).padStart(2, "0")}
              </div>
            </div>
            <h3 className="font-serif text-xl text-foreground">{item.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground/85">
              {item.body}
            </p>
            {item.safety && (
              <p className="mt-3 rounded-lg bg-white/60 dark:bg-white/10 p-2 text-xs text-[#8a6d3a] dark:text-[oklch(0.86_0.09_85)]">
                <span className="font-medium">Please note: </span>
                {item.safety}
              </p>
            )}
            <Link
              to={item.to as "/grounding"}
              hash={item.hash as string | undefined}
              className="mt-4 inline-flex w-fit items-center gap-1 rounded-full bg-[#8a6d3a] px-4 py-2 text-xs font-medium text-white shadow-sm transition hover:bg-[#725a2d]"
            >
              {item.buttonLabel} →
            </Link>
          </div>
        ))}
      />
    </section>
  );
}

// ---------------------- Closing note ----------------------

function ClosingNote() {
  return (
    <section
      aria-label="Personal note from Jana"
      className="mb-10 rounded-3xl border border-[#e6d7bd] bg-gradient-to-br from-[#fbf5ee] via-white dark:via-[oklch(0.26_0.02_80)] to-[#f4e9d6] p-6 shadow-sm sm:p-8 dark:border-white/12 dark:from-[oklch(0.26_0.02_80)] dark:to-[oklch(0.28_0.025_80)]"
    >
      <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/80 dark:bg-white/10 px-3 py-1 text-xs text-[#8a6d3a] ring-1 ring-[#e6d7bd] dark:text-[oklch(0.86_0.09_85)] dark:ring-white/12">
        <Heart className="h-3.5 w-3.5" /> A personal note from Jana
      </div>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-foreground/90">
        <p>
          If you are reading this during a difficult moment, I am glad you
          came here.
        </p>
        <p>
          I created Recovery Trauma™ because I understand how overwhelming
          emotional pain can feel. Recovery did not happen for me through one
          perfect technique. It happened through many small moments of
          pausing, reaching for support, expressing what I felt, and learning
          to respond to myself with more kindness.
        </p>
        <p className="font-medium">Your pain deserves care, not punishment.</p>
        <p>
          Please stay with yourself for this moment and reach out for human
          support when you need it.
        </p>
        <p className="font-serif text-xl text-[#8a6d3a] dark:text-[oklch(0.86_0.09_85)]">You are not alone.</p>
        <p className="pt-2 text-sm text-foreground/70">
          Jana Burkhardt
          <br />
          Founder, Recovery Trauma™
        </p>
      </div>
    </section>
  );
}

function FooterSafety({ onCrisis }: { onCrisis: () => void }) {
  return (
    <div className="rounded-2xl border border-[#e6d7bd] bg-white/70 dark:bg-white/10 p-4 text-center text-xs text-foreground/75 dark:border-white/12">
      If the urge feels unsafe to manage alone, please{" "}
      <button
        onClick={onCrisis}
        className="font-medium text-[#8a6d3a] underline underline-offset-2 dark:text-[oklch(0.86_0.09_85)]"
      >
        open crisis support
      </button>
      . Recovery Trauma™ is not a crisis service.
    </div>
  );
}

// ---------------------- Safety Plan ----------------------

const PLAN_FIELDS: { key: string; label: string; placeholder: string }[] = [
  { key: "triggers", label: "What usually triggers my urges?", placeholder: "e.g. loud conflict, feeling dismissed, certain anniversaries…" },
  { key: "warning_signs", label: "What signs tell me I am becoming overwhelmed?", placeholder: "e.g. tight chest, shallow breath, wanting to disappear…" },
  { key: "grounding_tools", label: "Which three grounding tools help me most?", placeholder: "e.g. cool water, 5-4-3-2-1, wrap in blanket…" },
  { key: "safer_places", label: "Which places feel safer?", placeholder: "e.g. the kitchen, my mum's house, the park bench…" },
  { key: "safe_people", label: "Who can I contact?", placeholder: "Names and how to reach them" },
  { key: "remove", label: "What can I move away from, or ask someone else to hold?", placeholder: "e.g. certain objects, my phone at night…" },
  { key: "words_i_need", label: "What would I like someone to say to me?", placeholder: "e.g. 'You're safe with me. I'm not going anywhere.'" },
  { key: "professionals", label: "Which professional services can I contact?", placeholder: "Therapist, GP, crisis line — name and number" },
  { key: "reminder", label: "What helps me remember that the urge can pass?", placeholder: "A memory, a phrase, a fact about my own strength…" },
];

type PlanValues = Record<string, string>;

type SyncState = "local" | "syncing" | "synced" | "error";

function useSafetyPlan() {
  const [values, setValues] = useState<PlanValues>({});
  const [loaded, setLoaded] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [sync, setSync] = useState<SyncState>("local");

  const readLocal = () => {
    try {
      const raw = localStorage.getItem(LS_PLAN);
      if (!raw) return null;
      return JSON.parse(raw) as { values?: PlanValues; savedAt?: number };
    } catch {
      return null;
    }
  };

  const writeLocal = (next: PlanValues, at: number) => {
    try {
      localStorage.setItem(LS_PLAN, JSON.stringify({ values: next, savedAt: at }));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const local = readLocal();
      if (!cancelled && local) {
        setValues(local.values ?? {});
        setSavedAt(local.savedAt ?? null);
      }

      let user: { id: string } | null = null;
      try {
        const { data } = await supabase.auth.getUser();
        user = data.user ?? null;
      } catch { /* offline / signed out */ }

      if (cancelled) return;
      if (!user) {
        setSignedIn(false);
        setLoaded(true);
        return;
      }

      setSignedIn(true);
      setSync("syncing");
      try {
        const { data, error } = await supabase
          .from("safety_plans")
          .select("answers, updated_at")
          .eq("user_id", user.id)
          .maybeSingle();
        if (error) throw error;
        if (cancelled) return;

        if (data) {
          const remote = (data.answers ?? {}) as PlanValues;
          const remoteAt = new Date(data.updated_at).getTime();
          const localAt = local?.savedAt ?? 0;
          if (remoteAt >= localAt) {
            setValues(remote);
            setSavedAt(remoteAt);
            writeLocal(remote, remoteAt);
          } else if (local?.values) {
            // Local copy is newer — push it up.
            await supabase
              .from("safety_plans")
              .upsert({ user_id: user.id, answers: local.values }, { onConflict: "user_id" });
          }
        } else if (local?.values && Object.keys(local.values).length > 0) {
          // First sign-in with an existing browser plan: adopt it into the account.
          await supabase
            .from("safety_plans")
            .upsert({ user_id: user.id, answers: local.values }, { onConflict: "user_id" });
        }
        if (!cancelled) setSync("synced");
      } catch {
        if (!cancelled) setSync("error");
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const save = async (next: PlanValues) => {
    const now = Date.now();
    setValues(next);
    setSavedAt(now);
    writeLocal(next, now);

    if (!signedIn) return;
    setSync("syncing");
    try {
      const { data } = await supabase.auth.getUser();
      const uid = data.user?.id;
      if (!uid) { setSync("local"); return; }
      const { error } = await supabase
        .from("safety_plans")
        .upsert({ user_id: uid, answers: next }, { onConflict: "user_id" });
      if (error) throw error;
      setSync("synced");
    } catch {
      setSync("error");
    }
  };

  const clear = async () => {
    setValues({});
    setSavedAt(null);
    try { localStorage.removeItem(LS_PLAN); } catch { /* ignore */ }
    if (!signedIn) return;
    try {
      const { data } = await supabase.auth.getUser();
      if (data.user?.id) {
        await supabase.from("safety_plans").delete().eq("user_id", data.user.id);
      }
      setSync("synced");
    } catch {
      setSync("error");
    }
  };

  return { values, save, clear, savedAt, loaded, signedIn, sync };
}


const escapeHtml = (s: string) =>
  s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c,
  );




function SafetyPlanSection({
  onCrisis,
  onOpenContacts,
}: {
  onCrisis: () => void;
  onOpenContacts: () => void;
}) {
  const { values, save, clear, savedAt, loaded, signedIn, sync } = useSafetyPlan();
  const hasSaved = savedAt !== null;
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [draft, setDraft] = useState<PlanValues>({});
  const [expanded, setExpanded] = useState(true);


  useEffect(() => {
    if (loaded && !hasSaved) setMode("edit");
  }, [loaded, hasSaved]);

  useEffect(() => {
    if (mode === "edit") setDraft(values);
  }, [mode, values]);

  const savedRelative = useMemo(() => {
    if (!savedAt) return null;
    const mins = Math.round((Date.now() - savedAt) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.round(hrs / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }, [savedAt]);

  return (
    <section
      id="safety-plan"
      aria-labelledby="plan-heading"
      className="mb-10 scroll-mt-24 rounded-3xl border border-[#e6d7bd] bg-white/80 dark:bg-white/10 p-6 shadow-sm sm:p-7 dark:border-white/12"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#fbf5ee] px-3 py-1 text-xs text-[#8a6d3a] ring-1 ring-[#e6d7bd] dark:bg-[oklch(0.26_0.02_80)] dark:text-[oklch(0.86_0.09_85)] dark:ring-white/12">
            <NotebookPen className="h-3.5 w-3.5" /> Private to you
          </div>
          <h2 id="plan-heading" className="font-serif text-3xl tracking-tight text-foreground">
            My Support Plan
          </h2>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground/75">
            A gentle place to write down what already helps you. When you are
            signed in it is saved privately to your profile, so you can come
            back and edit it on any device — never posted to the community,
            never visible to other members or staff.
          </p>

        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 rounded-full p-2 text-muted-foreground hover:bg-[#fbf5ee] hover:text-foreground dark:hover:bg-accent"
          aria-label={expanded ? "Collapse safety plan" : "Expand safety plan"}
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {expanded && (
        <>
          {mode === "view" && hasSaved ? (
            <div className="space-y-3">
              {PLAN_FIELDS.map((f) => {
                const v = (values[f.key] ?? "").trim();
                if (!v) return null;
                return (
                  <div key={f.key} className="rounded-xl bg-[#fbf5ee]/60 p-4 dark:bg-[oklch(0.26_0.02_80)]/60">
                    <div className="text-xs font-medium uppercase tracking-wider text-[#8a6d3a] dark:text-[oklch(0.86_0.09_85)]">
                      {f.label}
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                      {v}
                    </p>
                  </div>
                );
              })}
              {savedRelative && (
                <p className="text-center text-xs text-muted-foreground">
                  Last saved {savedRelative}
                  {signedIn && sync === "synced" && " · saved to your profile"}
                  {signedIn && sync === "syncing" && " · saving to your profile…"}
                  {signedIn && sync === "error" && " · saved in this browser only"}
                </p>
              )}

            </div>
          ) : (
            <div className="space-y-4">
              {PLAN_FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="mb-1 block text-sm font-medium text-foreground/90">
                    {f.label}
                  </span>
                  <textarea
                    value={draft[f.key] ?? ""}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, [f.key]: e.target.value.slice(0, 1000) }))
                    }
                    rows={3}
                    maxLength={1000}
                    placeholder={f.placeholder}
                    className="w-full rounded-xl border border-[#e6d7bd] bg-white/90 dark:bg-white/10 p-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-[#c9a24a] focus:outline-none focus:ring-2 focus:ring-[#c9a24a]/40 dark:border-white/12"
                  />
                </label>
              ))}
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {mode === "edit" ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    void save(draft);
                    setMode("view");
                  }}

                  className="inline-flex items-center gap-2 rounded-full bg-[#8a6d3a] px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#725a2d]"
                >
                  <Save className="h-4 w-4" /> Save My Support Plan
                </button>
                {hasSaved && (
                  <button
                    type="button"
                    onClick={() => setMode("view")}
                    className="rounded-full px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setMode("edit")}
                  className="inline-flex items-center gap-2 rounded-full bg-[#8a6d3a] px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-[#725a2d]"
                >
                  <NotebookPen className="h-4 w-4" /> Edit My Support Plan
                </button>
                <button
                  type="button"
                  onClick={onOpenContacts}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-medium text-[#8a6d3a] ring-1 ring-[#e6d7bd] hover:bg-[#fbf5ee] dark:bg-card dark:text-[oklch(0.86_0.09_85)] dark:ring-white/15 dark:hover:bg-card/80 dark:ring-white/12"
                >
                  <Users className="h-4 w-4" /> Contact Someone Safe
                </button>
                <button
                  type="button"
                  onClick={onCrisis}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-medium text-[#8a6d3a] ring-1 ring-[#e6d7bd] hover:bg-[#fbf5ee] dark:bg-card dark:text-[oklch(0.86_0.09_85)] dark:ring-white/15 dark:hover:bg-card/80 dark:ring-white/12"
                >
                  <LifeBuoy className="h-4 w-4" /> Crisis Support
                </button>

                {hasSaved && (
                  <button
                    type="button"
                    onClick={() => {
                      const msg = signedIn
                        ? "Delete your saved support plan from your profile and this browser?"
                        : "Delete your saved support plan from this browser?";
                      if (confirm(msg)) {
                        void clear();
                        setMode("edit");
                      }
                    }}

                    className="ms-auto inline-flex items-center gap-1 rounded-full px-3 py-2 text-xs text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete plan
                  </button>
                )}
              </>
            )}
          </div>




          <p className="mt-4 text-xs text-muted-foreground">
            {signedIn
              ? "Saved privately to your profile and kept in this browser too, so you can pick it up again later. Only you can read it — no other member or staff member can."
              : "Stored privately in this browser. Sign in and it will be saved to your profile so you can come back and edit it on any device. Nothing is shared."}
          </p>

        </>
      )}
    </section>
  );
}

// ---------------------- Contact Someone Safe ----------------------

interface SafeContact {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  note?: string;
}

const MESSAGE_TEMPLATES = [
  "I am having a difficult moment. Could you stay with me by phone or message?",
  "I do not need advice right now. I just need someone with me.",
  "I am struggling with urges and would feel safer if I were not alone.",
  "Could you help me contact professional support?",
];

function useSafeContacts() {
  const [contacts, setContacts] = useState<SafeContact[]>([]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_CONTACTS);
      if (raw) setContacts(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);
  const persist = (next: SafeContact[]) => {
    setContacts(next);
    try { localStorage.setItem(LS_CONTACTS, JSON.stringify(next)); } catch { /* ignore */ }
  };
  return {
    contacts,
    add: (c: Omit<SafeContact, "id">) =>
      persist([...contacts, { ...c, id: Math.random().toString(36).slice(2) }]),
    remove: (id: string) => persist(contacts.filter((c) => c.id !== id)),
  };
}

function ContactSafeSection() {
  const { contacts, add, remove } = useSafeContacts();
  const [selectedTemplate, setSelectedTemplate] = useState(MESSAGE_TEMPLATES[0]);
  const [customMessage, setCustomMessage] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [copied, setCopied] = useState(false);

  const message = customMessage.trim() || selectedTemplate;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const cleanName = name.trim().slice(0, 60);
  const cleanPhone = phone.trim().replace(/[^\d+()\-\s]/g, "").slice(0, 30);
  const cleanEmail = email.trim().slice(0, 120);
  const cleanNote = note.trim().slice(0, 120);
  const canAdd = cleanName.length > 0 && (cleanPhone.length > 0 || cleanEmail.length > 0);

  return (
    <section
      id="contact-safe"
      aria-labelledby="contact-heading"
      className="mb-12 scroll-mt-24 rounded-3xl border border-[#e6d7bd] bg-white/80 dark:bg-white/10 p-6 shadow-sm sm:p-7 dark:border-white/12"
    >
      <div className="mb-4">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#fbf5ee] px-3 py-1 text-xs text-[#8a6d3a] ring-1 ring-[#e6d7bd] dark:bg-[oklch(0.26_0.02_80)] dark:text-[oklch(0.86_0.09_85)] dark:ring-white/12">
          <Users className="h-3.5 w-3.5" /> Private to you
        </div>
        <h2 id="contact-heading" className="font-serif text-3xl tracking-tight text-foreground">
          Contact Someone Safe
        </h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground/75">
          Save the people you trust here, then choose a gentle message to send
          when you need company. Nothing is sent automatically — you always
          choose when to reach out.
        </p>
      </div>

      {/* Saved contacts */}
      <div className="mb-5">
        <div className="mb-2 text-xs font-medium uppercase tracking-wider text-[#8a6d3a] dark:text-[oklch(0.86_0.09_85)]">
          Your safe contacts
        </div>
        {contacts.length === 0 ? (
          <p className="rounded-xl bg-[#fbf5ee]/60 p-4 text-sm text-muted-foreground dark:bg-[oklch(0.26_0.02_80)]/60">
            You haven't saved anyone yet. Add one person below — even one is enough.
          </p>
        ) : (
          <ul className="space-y-2">
            {contacts.map((c) => (
              <li
                key={c.id}
                className="flex flex-wrap items-center gap-2 rounded-xl border border-[#ecdfc9] bg-white/90 dark:bg-white/10 p-3 dark:border-white/10"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{c.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {[c.phone, c.email, c.note].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {c.phone && (
                  <>
                    <a
                      href={`sms:${c.phone.replace(/[^\d+]/g, "")}?body=${encodeURIComponent(message)}`}
                      className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-[#8a6d3a] ring-1 ring-[#e6d7bd] hover:bg-[#fbf5ee] dark:bg-card dark:text-[oklch(0.86_0.09_85)] dark:ring-white/15 dark:hover:bg-card/80 dark:ring-white/12"
                    >
                      <MessageCircle className="h-3 w-3" /> Text
                    </a>
                  </>
                )}
                {c.email && (
                  <a
                    href={`mailto:${encodeURIComponent(c.email)}?subject=${encodeURIComponent("A message from me")}&body=${encodeURIComponent(message)}`}
                    className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-[#8a6d3a] ring-1 ring-[#e6d7bd] hover:bg-[#fbf5ee] dark:bg-card dark:text-[oklch(0.86_0.09_85)] dark:ring-white/15 dark:hover:bg-card/80 dark:ring-white/12"
                  >
                    <Mail className="h-3 w-3" /> Email
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Remove ${c.name} from your safe contacts?`)) remove(c.id);
                  }}
                  aria-label={`Remove ${c.name}`}
                  className="rounded-full p-1.5 text-muted-foreground hover:bg-[#fbf5ee] hover:text-destructive dark:hover:bg-accent"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add contact */}
      <details className="mb-5 rounded-xl bg-[#fbf5ee]/50 p-4 dark:bg-[oklch(0.26_0.02_80)]/50">
        <summary className="cursor-pointer text-sm font-medium text-[#8a6d3a] dark:text-[oklch(0.86_0.09_85)]">
          + Add a safe contact
        </summary>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 60))}
            maxLength={60}
            className="rounded-lg border border-[#e6d7bd] bg-white p-2.5 dark:border-white/15 dark:bg-card dark:text-foreground text-sm focus:border-[#c9a24a] focus:outline-none focus:ring-2 focus:ring-[#c9a24a]/40 dark:border-white/12"
          />
          <input
            type="text"
            placeholder="Note (e.g. 'Mum', 'Therapist')"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 120))}
            maxLength={120}
            className="rounded-lg border border-[#e6d7bd] bg-white p-2.5 dark:border-white/15 dark:bg-card dark:text-foreground text-sm focus:border-[#c9a24a] focus:outline-none focus:ring-2 focus:ring-[#c9a24a]/40 dark:border-white/12"
          />
          <input
            type="tel"
            placeholder="Phone (optional)"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/[^\d+()\-\s]/g, "").slice(0, 30))}
            maxLength={30}
            className="rounded-lg border border-[#e6d7bd] bg-white p-2.5 dark:border-white/15 dark:bg-card dark:text-foreground text-sm focus:border-[#c9a24a] focus:outline-none focus:ring-2 focus:ring-[#c9a24a]/40 dark:border-white/12"
          />
          <input
            type="email"
            placeholder="Email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value.slice(0, 120))}
            maxLength={120}
            className="rounded-lg border border-[#e6d7bd] bg-white p-2.5 dark:border-white/15 dark:bg-card dark:text-foreground text-sm focus:border-[#c9a24a] focus:outline-none focus:ring-2 focus:ring-[#c9a24a]/40 dark:border-white/12"
          />
        </div>
        <button
          type="button"
          disabled={!canAdd}
          onClick={() => {
            add({
              name: cleanName,
              phone: cleanPhone || undefined,
              email: cleanEmail || undefined,
              note: cleanNote || undefined,
            });
            setName(""); setPhone(""); setEmail(""); setNote("");
          }}
          className="mt-3 inline-flex items-center gap-1 rounded-full bg-[#8a6d3a] px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-[#725a2d] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Check className="h-3.5 w-3.5" /> Save contact
        </button>
        <p className="mt-2 text-xs text-muted-foreground">
          Contacts are stored privately in this browser only.
        </p>
      </details>

      {/* Message template */}
      <div className="rounded-xl bg-[#fbf5ee]/60 p-4 dark:bg-[oklch(0.26_0.02_80)]/60">
        <div className="mb-2 text-xs font-medium uppercase tracking-wider text-[#8a6d3a] dark:text-[oklch(0.86_0.09_85)]">
          Choose a gentle message
        </div>
        <div className="space-y-2">
          {MESSAGE_TEMPLATES.map((t) => (
            <label
              key={t}
              className={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 text-sm transition ${
                selectedTemplate === t
                  ? "border-[#c9a24a] bg-white dark:bg-card"
                  : "border-[#ecdfc9] bg-white/70 hover:bg-white dark:border-white/10 dark:bg-card/70 dark:hover:bg-card"
              }`}
            >
              <input
                type="radio"
                name="msg-template"
                checked={selectedTemplate === t && !customMessage.trim()}
                onChange={() => {
                  setSelectedTemplate(t);
                  setCustomMessage("");
                }}
                className="mt-0.5 accent-[#8a6d3a]"
              />
              <span className="text-foreground/90">{t}</span>
            </label>
          ))}
        </div>
        <label className="mt-3 block">
          <span className="mb-1 block text-xs font-medium text-foreground/80">
            Or write your own
          </span>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value.slice(0, 500))}
            maxLength={500}
            rows={2}
            placeholder="Just a few words in your own voice."
            className="w-full rounded-lg border border-[#e6d7bd] bg-white p-2.5 dark:border-white/15 dark:bg-card dark:text-foreground text-sm placeholder:text-muted-foreground/70 focus:border-[#c9a24a] focus:outline-none focus:ring-2 focus:ring-[#c9a24a]/40 dark:border-white/12"
          />
        </label>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-[#8a6d3a] ring-1 ring-[#e6d7bd] hover:bg-[#fbf5ee] dark:bg-card dark:text-[oklch(0.86_0.09_85)] dark:ring-white/15 dark:hover:bg-card/80 dark:ring-white/12"
          >
            <Copy className="h-3 w-3" /> {copied ? "Copied" : "Copy message"}
          </button>
          <span className="text-xs text-muted-foreground">
            Use the Text / Email buttons on a contact above. Nothing is
            sent until you confirm on your device.
          </span>
        </div>
      </div>
    </section>
  );
}

// ---------------------- Completion prompt ----------------------

function CompletionPrompt({
  onClose,
  onCrisis,
  onOpenPlan,
  onOpenContacts,
}: {
  onClose: () => void;
  onCrisis: () => void;
  onOpenPlan: () => void;
  onOpenContacts: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const options: { label: string; icon: typeof Leaf; onClick: () => void }[] = [
    { label: "Try another grounding tool", icon: Leaf, onClick: () => { onClose(); window.location.href = "/grounding"; } },
    { label: "Open my support plan", icon: NotebookPen, onClick: onOpenPlan },
    { label: "Contact someone safe", icon: Users, onClick: onOpenContacts },
    { label: "Rest for a few minutes", icon: Moon, onClick: onClose },
    { label: "Get crisis support", icon: LifeBuoy, onClick: onCrisis },
  ];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-[#2a1e0a]/40 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="completion-title"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-t-3xl border border-[#e6d7bd] bg-gradient-to-br from-white dark:from-[oklch(0.26_0.02_80)] via-[#fbf5ee] to-[#f4e9d6] p-6 shadow-2xl focus:outline-none sm:rounded-3xl sm:p-7 dark:border-white/12 dark:via-[oklch(0.26_0.02_80)] dark:to-[oklch(0.28_0.025_80)]"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute end-4 top-4 rounded-full p-1.5 text-muted-foreground hover:bg-white/70 dark:hover:bg-white/10 dark:bg-white/10 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/80 dark:bg-white/10 px-3 py-1 text-xs text-[#8a6d3a] ring-1 ring-[#e6d7bd] dark:text-[oklch(0.86_0.09_85)] dark:ring-white/12">
          <Sparkle className="h-3.5 w-3.5" /> A quiet pause
        </div>
        <h2 id="completion-title" className="font-serif text-2xl text-foreground sm:text-3xl">
          Thank you for staying with yourself through this moment.
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground/85">
          You do not have to feel completely better for this step to matter.
        </p>

        <div className="mt-5 space-y-2">
          {options.map((o) => {
            const Icon = o.icon;
            return (
              <button
                key={o.label}
                type="button"
                onClick={o.onClick}
                className="flex w-full items-center gap-3 rounded-2xl border border-[#ecdfc9] bg-white/90 px-4 py-3 text-start text-sm font-medium text-foreground shadow-sm transition hover:bg-white dark:border-white/10 dark:bg-card/80 dark:hover:bg-card"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#fbf5ee] text-[#8a6d3a] dark:bg-[oklch(0.26_0.02_80)] dark:text-[oklch(0.86_0.09_85)]">
                  <Icon className="h-4 w-4" />
                </span>
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
